import React from 'react';
import { Grid, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import './Benefits.scss';
import { CreditCardTier } from 'types/dto/profile';
import blueIcon from 'assets/images/credit_card_blue.svg';
import goldIcon from 'assets/images/credit_card_golden.svg';
import silverIcon from 'assets/images/credit_card_silver.svg';
import platinumIcon from 'assets/images/credit_card_platinum.svg';
import { useSession } from 'context/SessionContext';



interface BenefitRow {
  key: string;
  name: React.ReactNode;
  blue: React.ReactNode;
  silver: React.ReactNode;
  gold: React.ReactNode;
  platinum: React.ReactNode;
  isSection?: boolean;
  isSubRow?: boolean;
  isFootnote?: boolean;
}

const Benefits: React.FC = () => {
  const { t } = useTranslation('benefits');
  const screens = Grid.useBreakpoint();
  const { session } = useSession();

  const currentTier = (session?._ti?.toLowerCase().split('-').pop() as CreditCardTier) || 'blue';

  const renderTierHeader = (tierKey: CreditCardTier, icon: string) => (
    <div className="benefits-tier-header">
      <div className="benefits-tier-name">{t(tierKey)}</div>
      <div className="current-tier">{currentTier === tierKey ? t('current') : ' '}</div>
      <img src={icon} alt={tierKey} />
    </div>
  );

  const tipsSeparator = '#$#';

  // Function to handle cell visibility for section titles and footnotes
  const handleCellVisibility = (record: BenefitRow) => {
    if (record.isSection || record.isFootnote) {
      return { colSpan: 0 };
    }
    return {};
  };


  // Create data rows
  const dataSource: BenefitRow[] = [

    //section 1
    {
      key: 'birthday-discount',
      name: t('birthdayDiscount'),
      blue: t('val_5pct'),
      silver: t('val_5pct'),
      gold: t('val_5pct'),
      platinum: t('val_5pct'),
    },
    {
      key: 'laundry-discount',
      name: t('laundryDiscount'),
      blue: '-',
      silver: t('val_10pct'),
      gold: t('val_20pct'),
      platinum: t('val_30pct'),
    },
    {
      key: 'merchandise-discount',
      name: t('merchandiseDiscount'),
      blue: '-',
      silver: '-',
      gold: t('val_5pct'),
      platinum: t('val_10pct'),
    },
    {
      key: 'pickup-discount',
      name: t('pickupServiceDiscount'),
      blue: '-',
      silver: '-',
      gold: t('val_5pct'),
      platinum: t('val_10pct'),
    },
    {
      key: 'hangers-discount',
      name: (
        <>
          {t('hangersReturnDiscount')}
          <sup>{t('note2')}</sup>
        </>
      ),
      blue: '$2',
      silver: '$2',
      gold: '$5',
      platinum: '$5',
    },
    {
      key: 'referral-rewards',
      name: t('referralRewards'),
      blue: '-',
      silver: '$20',
      gold: '$50',
      platinum: '$100',
    },
    {
      key: 'footnote-1',
      name: t('benefitsFootnote1') + tipsSeparator + t('benefitsFootnote2') ,
      blue: '',
      silver: '',
      gold: '',
      platinum: '',
      isFootnote: true,
    },

    //section 2: How to join section
    {
      key: 'section-join',
      name: (
        <>
          {t('howToJoin')}
          <sup>{t('note3')}</sup>
        </>
      ),
      blue: '',
      silver: '',
      gold: '',
      platinum: '',
      isSection: true,
    },
    {
      key: 'subscribe',
      name: t('subscribeMembership'),
      blue: t('val_signUpFree'),
      silver: t('val_silverSubscribe'),
      gold:  t('val_goldSubscribe'),
      platinum: t('val_byUpgradeOnly'),
    },
    {
      key: 'free-upgrade',
      name: t('freeUpgrade'),
      blue: '-',
      silver: (
        <>
          {t('val_siliverFreeUpgrade')}<sup>{t('note4')}</sup>
        </>
      ),
      gold: (
        <>
          {t('val_goldFreeUpgrade')}<sup>{t('note4')}</sup>
        </>
      ),
      platinum: (
        <>
          {t('val_platinumFreeUpgrade')}<sup>{t('note4')}</sup>
        </>
      ),
    },
    {
      key: 'footnote-2',
      name: t('benefitsFootnote3') + tipsSeparator + t('benefitsFootnote4') ,
      blue: '',
      silver: '',
      gold: '',
      platinum: '',
      isFootnote: true,
    },

    //section 3: Welcome gifts section
    {
      key: 'section-gifts',
      name: t('welcomeGifts'),
      blue: '',
      silver: '',
      gold: '',
      platinum: '',
      isSection: true,
    },
    {
      key: 'coupon-cash',
      name: (
        <>
          {t('coupon')}
          <sup>{t('note5')}</sup>
        </>
      ),
      blue: (
        <div>{t('val_cashCoupon5_1pc')}</div>
      ),
      silver: t('val_cashCoupon100_3pc'),
      gold: t('val_cashCoupon100_8pc'),
      platinum: '-',
    },
    {
      key: 'coupon-discount',
      name: (
        <>

        </>
      ),
      blue: (
        <div>
          {t('val_discountCoupon8pct_1pc')}<sup>{t('note6')}</sup>
        </div>
      ),
      silver: '',
      gold: '',
      platinum: '',
    },
    {
      key: 'free-laundry',
      name: (
        <>
          {t('freeLaundry')}
          <sup>{t('note7')}</sup>
        </>
      ),
      blue: '',
      silver: '',
      gold: '',
      platinum: '',
    },
    // Free laundry sub-rows
    {
      key: 'sub-shirt',
      name: t('shirtOrBlouse'),
      blue: (<div className='blue-circle'>
        ⬤
      </div>),
      silver: (<div className='silver-circle'>
        ⬤
      </div>),
      gold: '',
      platinum: (<div className='platinum-circle'>
        ⬤
      </div>),
      isSubRow: true,
    },
    {
      key: 'sub-trouser',
      name: t('trouser'),
      blue: '',
      silver: (<div className='silver-circle'>
        ⬤
      </div>),
      gold: '',
      platinum: '',
      isSubRow: true,
    },
    {
      key: 'sub-jacket',
      name: t('jacket'),
      blue: '',
      silver: '',
      gold: (<div className='gold-circle'>
        ⬤
      </div>),
      platinum: (<div className='platinum-circle'>
        ⬤
      </div>),
      isSubRow: true,
    },
    {
      key: 'sub-sweater',
      name: t('sweater'),
      blue: '',
      silver: '',
      gold: (<div className='gold-circle'>
        ⬤
      </div>),
      platinum: (<div className='platinum-circle'>
        ⬤
      </div>),
      isSubRow: true,
    },
    {
      key: 'footnote-3',
      name: t('benefitsFootnote5') + tipsSeparator + t('benefitsFootnote6') + tipsSeparator + t('benefitsFootnote7') ,
      blue: '',
      silver: '',
      gold: '',
      platinum: '',
      isFootnote: true,
    },
  ];

  // Define columns
  const columns: ColumnsType<BenefitRow> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: screens.xs ? 150 : 250,
      className: 'benefits-name-column',
      onCell: (record) => {
        // Section titles and footnotes span all columns
        if ( record.isFootnote) {
          return {
            colSpan: 5,
            className: record.isSection
              ? 'benefits-section-cell'
              : 'benefits-footnote-cell',
          };
        }
        // Sub-rows
        if (record.isSubRow) {
          return {className: 'benefits-sub-row-cell',};
        }
        return {};
      },
      render: (text, record) => {
        if (record.isFootnote) {
          // Split text by tipsSeparator and render each part as a separate div
          const parts = String(text).split(tipsSeparator).filter(part => part.trim() !== '');

          // Calculate the starting counter for this footnote based on previous footnotes
          let currentCounter = 1;
          const currentIndex = dataSource.findIndex(item => item.key === record.key);

          // Count all footnote parts before current footnote
          for (let i = 0; i < currentIndex; i++) {
            if (dataSource[i].isFootnote) {
              const prevParts = String(dataSource[i].name).split(tipsSeparator).filter(part => part.trim() !== '');
              currentCounter += prevParts.length;
            }
          }

          return (
            <div className='footer-note'>
              {parts.map((part, index) => {
                const counter = currentCounter + index;
                return (
                  <div key={index}><sup>{counter}</sup>{part.trim()}</div>
                );
              })}
            </div>
          );
        }
        return text;
      },
    },
    {
      title: renderTierHeader('blue', blueIcon),
      dataIndex: 'blue',
      key: 'blue',
      width: 150,
      className: currentTier === 'blue' ? 'benefits-active-column' : '',
      onCell: (record) => {
        // Hide cell for section titles and footnotes (they span from first column)
        return handleCellVisibility(record);
      },
    },
    {
      title: renderTierHeader('silver', silverIcon),
      dataIndex: 'silver',
      key: 'silver',
      width: 150,
      className: currentTier === 'silver' ? 'benefits-active-column' : '',
      onCell: (record) => {
        // Hide cell for section titles and footnotes
        return handleCellVisibility(record);
      },
    },
    {
      title: renderTierHeader( 'gold', goldIcon),
      dataIndex: 'gold',
      key: 'gold',
      width: 150,
      className: currentTier === 'gold' ? 'benefits-active-column' : '',
      onCell: (record) => {
        // Hide cell for section titles and footnotes
        return handleCellVisibility(record);
      },
    },
    {
      title: renderTierHeader('platinum', platinumIcon),
      dataIndex: 'platinum',
      key: 'platinum',
      width: 150,
      className: currentTier === 'platinum' ? 'benefits-active-column' : '',
      onCell: (record) => {
        // Hide cell for section titles and footnotes
        return handleCellVisibility(record);
      },
    },
  ];

  return (
    <div className="benefits">
      <Table
        title={() => (
          <div className='benefits-title'>
            {t('membershipBenefits')}
            <sup>{t('note1')}</sup>
          </div>
        )}
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        className="benefits-table"
      />
    </div>
  );
};

export default Benefits;

